<template>
  <section class="coaching-page">
    <header class="hero-card">
      <div class="hero-heading">
        <v-btn icon class="icon-action academy-local-back" aria-label="Zurück zu Leadership" to="/akademie/leadership"
          ><v-icon>mdi-arrow-left</v-icon></v-btn
        >
        <span class="hero-icon"><v-icon>mdi-account-supervisor-circle-outline</v-icon></span>
        <div class="hero-copy">
          <div class="eyebrow">Individuelles Sparring</div>
          <h2>Leadership Coaching 1:1</h2>
          <p>Buche eine freie 1:1-Session für Führungsfragen, Gesprächsvorbereitung oder aktuelle Herausforderungen.</p>
        </div>
      </div>
      <v-btn outlined color="#c4b5fd" class="extra-action" :disabled="!settingsLoaded" @click="extraDialog = true">
        <v-icon left>mdi-calendar-star</v-icon>Extra-Session anfragen
      </v-btn>
    </header>

    <section class="content-section booking-section">
      <div class="section-header">
        <div>
          <div class="eyebrow">Deine Termine</div>
          <h3>Meine Buchungen</h3>
        </div>
        <span class="count-badge">{{ activeBookings.length }} {{ activeBookings.length === 1 ? "Eintrag" : "Einträge" }}</span>
      </div>

      <div v-if="loadingBookings" class="booking-grid">
        <v-skeleton-loader v-for="index in 2" :key="index" dark type="list-item-three-line, actions" class="skeleton-card" />
      </div>
      <div v-else-if="activeBookings.length" class="booking-grid">
        <article v-for="booking in activeBookings" :key="booking._id" class="booking-card" :class="`is-${booking.status || 'confirmed'}`">
          <header class="booking-card-header">
            <span class="date-tile"
              ><strong>{{ formatDayNumber(booking.startsAt) }}</strong
              ><small>{{ formatMonth(booking.startsAt) }}</small></span
            >
            <div class="booking-copy">
              <div class="booking-time-row">
                <span>{{ formatWeekday(booking.startsAt) }} · {{ formatTimeRange(booking.startsAt, booking.endsAt) }}</span>
                <span class="booking-status" :class="`status-${booking.status || 'confirmed'}`">
                  <v-icon>{{ bookingStatusIcon(booking.status) }}</v-icon>{{ bookingStatusLabel(booking.status) }}
                </span>
              </div>
              <h4>{{ booking.topic || "Leadership Coaching" }}</h4>
              <p v-if="booking.center || booking.function">
                <span v-if="booking.center"><v-icon>mdi-map-marker-outline</v-icon>{{ booking.center }}</span>
                <span v-if="booking.function"><v-icon>mdi-account-tie-outline</v-icon>{{ booking.function }}</span>
              </p>
            </div>
          </header>

          <div class="booking-state" :class="`state-${booking.status || 'confirmed'}`">
            <v-icon>{{ bookingStatusIcon(booking.status) }}</v-icon>
            <div>
              <strong>{{ bookingStateTitle(booking) }}</strong>
              <span>{{ bookingStateText(booking) }}</span>
            </div>
          </div>

          <footer class="booking-actions">
            <v-btn
              v-if="booking.status === 'confirmed' && (booking.joinUrl || booking.teamsJoinUrl)"
              :href="booking.joinUrl || booking.teamsJoinUrl"
              target="_blank"
              rel="noopener noreferrer"
              color="#009640"
              depressed
              class="teams-action">
              <v-icon left>mdi-microsoft-teams</v-icon>Teams öffnen
            </v-btn>
            <v-btn
              v-if="canRequestCancellation(booking)"
              text
              color="#ff8d98"
              class="cancel-action"
              :disabled="!canCancelBooking(booking)"
              :title="cancelTitle(booking)"
              @click="askCancel(booking)">
              {{ cancelLabel(booking) }}
            </v-btn>
          </footer>
        </article>
      </div>
      <div v-else class="empty-inline">
        <v-icon>mdi-calendar-blank-outline</v-icon>
        <span>Du hast aktuell keine bevorstehende 1:1-Session.</span>
      </div>
    </section>

    <section class="content-section slots-section">
      <div class="section-header slots-header">
        <div>
          <div class="eyebrow">Flexible Terminlänge</div>
          <h3>Freie Sessions buchen</h3>
          <p>Freie und bereits belegte Zeitfenster sind direkt ersichtlich.</p>
          <div v-if="settingsLoaded" class="policy-row">
            <span><v-icon>mdi-calendar-range</v-icon>{{ bookingHorizonWeeks }} Wochen im Voraus</span>
            <span><v-icon>mdi-clock-alert-outline</v-icon>{{ minimumLeadHours }} Std. Mindestvorlauf</span>
            <span><v-icon>mdi-calendar-remove-outline</v-icon>Storno bis {{ cancellationDeadlineHours }} Std. vorher</span>
          </div>
        </div>
        <div class="week-controls" aria-label="Wochen wechseln">
          <v-btn icon class="small-icon-action" aria-label="Vorherige Woche" :disabled="!canGoBack" @click="changeWeek(-7)"
            ><v-icon>mdi-chevron-left</v-icon></v-btn
          >
          <span>{{ weekLabel }}</span>
          <v-btn icon class="small-icon-action" aria-label="Nächste Woche" :disabled="!canGoForward" @click="changeWeek(7)"
            ><v-icon>mdi-chevron-right</v-icon></v-btn
          >
        </div>
      </div>

      <div v-if="loadingSlots" class="slot-browser-skeleton">
        <v-skeleton-loader v-for="index in 2" :key="index" dark type="heading, list-item@3" class="skeleton-card" />
      </div>
      <div v-else-if="sessionDays.length && selectedDay" class="slot-browser">
        <div class="day-selector" role="tablist" aria-label="Tage mit Sessions">
          <button
            v-for="day in sessionDays"
            :key="day.date"
            type="button"
            role="tab"
            class="day-option"
            :class="{ active: selectedDay.date === day.date, full: freeCount(day) === 0 }"
            :aria-selected="selectedDay.date === day.date ? 'true' : 'false'"
            :aria-label="dayAriaLabel(day)"
            @click="selectDay(day.date)">
            <span>{{ formatDayName(day.date) }}</span>
            <strong>{{ formatCompactDate(day.date) }}</strong>
            <small><i aria-hidden="true"></i>{{ dayAvailabilityLabel(day) }}</small>
          </button>
        </div>

        <article class="selected-day-card">
          <div class="selected-day-heading">
            <div>
              <span>Ausgewählter Tag</span>
              <h4>{{ formatDate(selectedDay.date) }}</h4>
            </div>
            <small>{{ dayAvailabilityLabel(selectedDay) }}</small>
          </div>
          <div class="slot-grid" aria-label="Sessions des gewählten Tages">
            <button
              v-for="slot in selectedDay.slots"
              :key="slot.startsAt"
              type="button"
              class="slot-button"
              :class="{ available: isAvailable(slot), occupied: !isAvailable(slot) }"
              :disabled="!isAvailable(slot)"
              @click="openBooking(slot)">
              <span class="slot-time">
                {{ formatTime(slot.startsAt) }}
                <small>{{ slotDuration(slot) }} Min.</small>
              </span>
              <span class="slot-status">
                <small>{{ isAvailable(slot) ? "Frei" : "Belegt" }}</small>
                <v-icon>{{ isAvailable(slot) ? "mdi-chevron-right" : "mdi-lock-outline" }}</v-icon>
              </span>
            </button>
          </div>
        </article>
      </div>
      <div v-else class="empty-state">
        <span><v-icon>mdi-calendar-remove-outline</v-icon></span>
        <h4>Keine Zeitfenster in dieser Woche</h4>
        <p>Wechsle zur nächsten Woche oder frage eine Extra-Session an.</p>
      </div>
    </section>

    <section v-if="extraRequests.length" class="content-section request-section">
      <div class="section-header">
        <div>
          <div class="eyebrow">Extra Coaching</div>
          <h3>Meine Anfragen</h3>
        </div>
      </div>
      <div class="request-list">
        <article v-for="request in extraRequests" :key="request._id" class="request-card">
          <span class="request-icon"><v-icon>mdi-calendar-star</v-icon></span>
          <div>
            <strong>{{ extraStatusLabel(request.status) }}</strong>
            <p v-if="requestDate(request)">{{ formatFullDate(requestDate(request)) }} · {{ requestDuration(request) }} Minuten</p>
            <p v-else>Angefragt am {{ formatFullDate(request.createdAt) }}</p>
            <small v-if="request.adminNote">{{ request.adminNote }}</small>
          </div>
          <div class="request-side">
            <span class="request-status" :class="`status-${request.status || 'submitted'}`">{{ extraStatusLabel(request.status) }}</span>
            <v-btn
              v-if="requestJoinUrl(request)"
              :href="requestJoinUrl(request)"
              target="_blank"
              rel="noopener noreferrer"
              text
              color="#58d388"
              class="request-link">
              <v-icon left>mdi-microsoft-teams</v-icon>Teams
            </v-btn>
          </div>
        </article>
      </div>
    </section>

    <v-dialog
      v-model="bookingDialog"
      max-width="34rem"
      dark
      persistent
      scrollable
      :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card dark class="dialog-card">
        <v-card-title class="dialog-title">
          <span><v-icon>mdi-calendar-check-outline</v-icon></span>
          <div>
            <small>{{ selectedSlot ? `${slotDuration(selectedSlot)}-Minuten-Session` : "Leadership Coaching 1:1" }}</small>
            <h3>{{ selectedSlot ? formatFullDate(selectedSlot.startsAt) : "" }}</h3>
          </div>
          <v-spacer />
          <v-btn icon aria-label="Dialog schliessen" :disabled="savingBooking" @click="bookingDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="dialog-body">
          <v-form ref="bookingForm" v-model="bookingValid">
            <v-textarea
              v-model="bookingForm.topic"
              label="Thema oder Frage"
              hint="Wobei wünschst du dir Sparring oder Unterstützung?"
              persistent-hint
              outlined
              rows="3"
              counter="600"
              :rules="topicRules" />
            <v-textarea
              v-model="bookingForm.goals"
              label="Ziele und Erwartungen"
              hint="Was möchtest du aus dieser Session mitnehmen?"
              persistent-hint
              outlined
              rows="3"
              counter="1200"
              :rules="goalsRules" />
            <div class="form-grid">
              <v-text-field v-model="bookingForm.center" label="Center" outlined :rules="centerRules" />
              <v-text-field v-model="bookingForm.function" label="Funktion (optional)" outlined />
            </div>
            <v-alert v-if="dialogError" type="error" text outlined>{{ dialogError }}</v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-btn text color="#d4d4d8" :disabled="savingBooking" @click="bookingDialog = false">Abbrechen</v-btn>
          <v-btn color="#009640" depressed class="primary-action" :loading="savingBooking" :disabled="!bookingValid" @click="bookSlot">
            <v-icon left>mdi-check</v-icon>Verbindlich buchen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialog" max-width="28rem" dark>
      <v-card dark class="dialog-card confirm-card">
        <span class="warning-icon"><v-icon>mdi-calendar-remove-outline</v-icon></span>
        <h3>Termin wirklich stornieren?</h3>
        <p v-if="cancelTarget">{{ formatFullDate(cancelTarget.startsAt) }}</p>
        <div>
          <v-btn text color="#d4d4d8" :disabled="cancelling" @click="cancelDialog = false">Behalten</v-btn>
          <v-btn color="#d94b5b" depressed :loading="cancelling" @click="cancelBooking">Stornieren</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <LeadershipExtraRequestDialog v-model="extraDialog" :minimum-lead-hours="minimumLeadHours || 0" @submitted="handleExtraSubmitted" />
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top right>{{ snackbar.text }}</v-snackbar>
  </section>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import LeadershipExtraRequestDialog from "./LeadershipExtraRequestDialog.vue"

function localDate(date) {
  const pad = (value) => String(value).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function unwrapList(result, key) {
  if (Array.isArray(result)) return result
  return (result && Array.isArray(result[key]) && result[key]) || []
}

function durationMinutes(item, fallback = 60) {
  const explicitMinutes = Number(item && item.slotMinutes)
  if ([30, 60].includes(explicitMinutes)) return explicitMinutes
  const startsAt = new Date(item && item.startsAt).getTime()
  const endsAt = new Date(item && item.endsAt).getTime()
  const difference = Math.round((endsAt - startsAt) / 60000)
  return Number.isInteger(difference) && difference > 0 ? difference : fallback
}

export default {
  name: "LeadershipCoachingOneToOne",
  components: { LeadershipExtraRequestDialog },
  data() {
    return {
      fromDate: localDate(new Date()),
      availabilityTo: "",
      bookingHorizonWeeks: null,
      minimumLeadHours: null,
      cancellationDeadlineHours: null,
      settingsLoaded: false,
      days: [],
      selectedDate: "",
      bookings: [],
      extraRequests: [],
      loadingSlots: false,
      loadingBookings: false,
      bookingDialog: false,
      bookingValid: false,
      savingBooking: false,
      selectedSlot: null,
      bookingForm: { topic: "", goals: "", center: "", function: "" },
      topicRules: [
        (value) => Boolean(value && value.trim()) || "Bitte gib ein Thema an",
        (value) => !value || value.length <= 600 || "Maximal 600 Zeichen",
      ],
      goalsRules: [
        (value) => Boolean(value && value.trim()) || "Bitte gib deine Ziele und Erwartungen an",
        (value) => !value || value.length <= 1200 || "Maximal 1200 Zeichen",
      ],
      centerRules: [(value) => Boolean(value && value.trim()) || "Bitte gib dein Center an"],
      dialogError: "",
      cancelDialog: false,
      cancelTarget: null,
      cancelling: false,
      extraDialog: false,
      snackbar: { show: false, color: "success", text: "" },
    }
  },
  computed: {
    isLeadershipManager() {
      return userService.hasRight("aka_leadership_manage")
    },
    activeBookings() {
      return this.bookings
        .filter((booking) => {
          if (["cancelled", "canceled"].includes(booking.status)) return false
          const startsAt = new Date(booking.startsAt).getTime()
          const endsAt = new Date(booking.endsAt).getTime()
          const effectiveEnd = Number.isFinite(endsAt) ? endsAt : startsAt + durationMinutes(booking, 30) * 60 * 1000
          return Number.isFinite(effectiveEnd) && effectiveEnd >= Date.now()
        })
        .sort((first, second) => new Date(first.startsAt) - new Date(second.startsAt))
    },
    sessionDays() {
      return this.days.filter((day) => Array.isArray(day.slots) && day.slots.length > 0)
    },
    selectedDay() {
      return this.sessionDays.find((day) => day.date === this.selectedDate) || this.sessionDays[0] || null
    },
    canGoBack() {
      const target = new Date(`${this.fromDate}T12:00:00`)
      target.setDate(target.getDate() - 7)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return target >= today
    },
    canGoForward() {
      if (!this.availabilityTo) return false
      const target = new Date(`${this.fromDate}T12:00:00`)
      target.setDate(target.getDate() + 7)
      return localDate(target) <= this.availabilityTo
    },
    weekLabel() {
      if (!this.days.length) return this.formatDate(this.fromDate)
      const first = this.days[0].date
      const last = this.days[this.days.length - 1].date
      return first === last ? this.formatDate(first) : `${this.formatShortDate(first)} - ${this.formatShortDate(last)}`
    },
  },
  mounted() {
    this.prefillUser()
    this.loadAll()
  },
  methods: {
    notify(color, text) {
      this.snackbar = { show: true, color, text }
    },
    prefillUser() {
      const user = userService.getUser() || {}
      const studio = user.studio || user.studio_location || {}
      this.bookingForm.center = typeof studio === "string" ? studio : studio.name || studio.studioName || studio.bezeichnung || ""
      this.bookingForm.function = user.function || user.funktion || user.position || user.employeeFunction || ""
    },
    async loadAll() {
      await Promise.all([this.loadSlots(), this.loadBookings(), this.loadExtraRequests()])
    },
    async loadSlots() {
      this.loadingSlots = true
      try {
        const result = await api.akademie.fetchLeadershipCoachingSlots({ from: this.fromDate })
        this.availabilityTo = (result && result.to) || this.availabilityTo
        if (result && Number.isFinite(Number(result.bookingHorizonWeeks))) this.bookingHorizonWeeks = Number(result.bookingHorizonWeeks)
        if (result && Number.isFinite(Number(result.minimumLeadHours))) this.minimumLeadHours = Number(result.minimumLeadHours)
        if (result && Number.isFinite(Number(result.cancellationDeadlineHours)))
          this.cancellationDeadlineHours = Number(result.cancellationDeadlineHours)
        this.settingsLoaded = [this.bookingHorizonWeeks, this.minimumLeadHours, this.cancellationDeadlineHours].every((value) =>
          Number.isFinite(value),
        )
        this.days = ((result && result.days) || []).slice(0, 7)
        this.syncSelectedDay()
      } catch (error) {
        this.days = []
        this.selectedDate = ""
        this.settingsLoaded = false
        this.notify("error", "Die freien Termine konnten nicht geladen werden.")
      } finally {
        this.loadingSlots = false
      }
    },
    async loadBookings() {
      this.loadingBookings = true
      try {
        const result = await api.akademie.fetchMyLeadershipCoachingBookings()
        this.bookings = unwrapList(result, "bookings")
      } catch (error) {
        this.bookings = []
      } finally {
        this.loadingBookings = false
      }
    },
    async loadExtraRequests() {
      try {
        const result = await api.akademie.fetchMyLeadershipExtraRequests()
        this.extraRequests = unwrapList(result, "requests")
      } catch (error) {
        this.extraRequests = []
      }
    },
    changeWeek(days) {
      if ((days < 0 && !this.canGoBack) || (days > 0 && !this.canGoForward)) return
      const date = new Date(`${this.fromDate}T12:00:00`)
      date.setDate(date.getDate() + days)
      this.fromDate = localDate(date)
      this.selectedDate = ""
      this.loadSlots()
    },
    syncSelectedDay() {
      if (this.sessionDays.some((day) => day.date === this.selectedDate)) return
      const firstAvailableDay = this.sessionDays.find((day) => this.freeCount(day) > 0)
      this.selectedDate = (firstAvailableDay || this.sessionDays[0] || {}).date || ""
    },
    selectDay(date) {
      if (this.sessionDays.some((day) => day.date === date)) this.selectedDate = date
    },
    isAvailable(slot) {
      return ["available", "free"].includes(slot.status)
    },
    slotDuration(slot) {
      return durationMinutes(slot)
    },
    freeCount(day) {
      return (day.slots || []).filter((slot) => this.isAvailable(slot)).length
    },
    dayAvailabilityLabel(day) {
      const count = this.freeCount(day)
      return count ? `${count} frei` : "Ausgebucht"
    },
    dayAriaLabel(day) {
      const count = this.freeCount(day)
      return `${this.formatDayName(day.date)}, ${this.formatDate(day.date)}, ${count} ${count === 1 ? "freie Session" : "freie Sessions"}`
    },
    openBooking(slot) {
      this.selectedSlot = slot
      this.bookingForm.topic = ""
      this.bookingForm.goals = ""
      this.dialogError = ""
      this.bookingDialog = true
      this.$nextTick(() => this.$refs.bookingForm && this.$refs.bookingForm.resetValidation())
    },
    async bookSlot() {
      if (!this.selectedSlot || !this.$refs.bookingForm.validate()) return
      this.savingBooking = true
      this.dialogError = ""
      try {
        await api.akademie.bookLeadershipCoachingSlot(this.selectedSlot.startsAt, {
          topic: this.bookingForm.topic.trim(),
          goals: this.bookingForm.goals.trim(),
          center: this.bookingForm.center.trim(),
          function: this.bookingForm.function.trim() || undefined,
        })
        this.bookingDialog = false
        this.notify("success", "Deine 1:1-Session wurde gebucht. Der Teams-Link ist bei deinem Termin hinterlegt.")
        await Promise.all([this.loadSlots(), this.loadBookings()])
      } catch (error) {
        const data = error.response && error.response.data
        if (data && data.booking) {
          this.bookingDialog = false
          this.selectedSlot = null
          this.notify("warning", "Dein Termin ist bestätigt. Der Teams-Link wird von der Academy-Leitung nachgereicht.")
          await Promise.all([this.loadSlots(), this.loadBookings()])
        } else {
          this.dialogError = (data && (data.error || data.message)) || "Dieses Zeitfenster konnte nicht gebucht werden. Bitte wähle ein anderes."
        }
      } finally {
        this.savingBooking = false
      }
    },
    askCancel(booking) {
      if (!this.canCancelBooking(booking)) return
      this.cancelTarget = booking
      this.cancelDialog = true
    },
    canRequestCancellation(booking) {
      return !["declined", "cancelled", "canceled"].includes(booking.status)
    },
    canCancelBooking(booking) {
      if (!this.canRequestCancellation(booking)) return false
      if (this.isLeadershipManager) return true
      if (!this.settingsLoaded) return false
      return new Date(booking.startsAt).getTime() - Date.now() >= this.cancellationDeadlineHours * 60 * 60 * 1000
    },
    cancelLabel(booking) {
      if (!this.settingsLoaded) return "Stornofrist wird geladen"
      if (!this.canCancelBooking(booking)) return "Stornofrist abgelaufen"
      return "Stornieren"
    },
    cancelTitle(booking) {
      if (!this.settingsLoaded) return "Stornofrist wird geladen"
      if (!this.canCancelBooking(booking)) return `Stornofrist von ${this.cancellationDeadlineHours} Stunden ist abgelaufen`
      return "Termin stornieren"
    },
    async cancelBooking() {
      if (!this.cancelTarget) return
      this.cancelling = true
      try {
        await api.akademie.cancelLeadershipCoachingBooking(this.cancelTarget._id)
        this.cancelDialog = false
        this.notify("success", "Der Termin wurde storniert.")
        await Promise.all([this.loadSlots(), this.loadBookings()])
      } catch (error) {
        const message = error.response && error.response.data && (error.response.data.error || error.response.data.message)
        this.notify("error", message || "Der Termin konnte nicht storniert werden.")
      } finally {
        this.cancelling = false
        this.cancelTarget = null
      }
    },
    handleExtraSubmitted(request) {
      if (request) this.extraRequests.unshift(request)
      this.notify("success", "Deine Anfrage wurde an die Academy-Leitung gesendet.")
      this.loadExtraRequests()
    },
    extraStatusLabel(status) {
      const labels = { submitted: "Offen", confirmed: "Bestätigt", declined: "Abgelehnt", needs_info: "Rückfrage", cancelled: "Storniert" }
      return labels[status || "submitted"] || "Offen"
    },
    bookingStatusLabel(status) {
      const labels = {
        approval_pending: "Bestätigt",
        meeting_pending: "Bestätigt",
        confirmed: "Bestätigt",
        meeting_failed: "Bestätigt",
        declined: "Abgelehnt",
        cancelled: "Storniert",
        canceled: "Storniert",
      }
      return labels[status || "confirmed"] || "Bestätigt"
    },
    bookingStatusIcon(status) {
      const icons = {
        approval_pending: "mdi-link-variant-plus",
        meeting_pending: "mdi-check-circle-outline",
        confirmed: "mdi-check-circle-outline",
        meeting_failed: "mdi-link-variant-off",
        declined: "mdi-close-circle-outline",
        cancelled: "mdi-calendar-remove-outline",
        canceled: "mdi-calendar-remove-outline",
      }
      return icons[status || "confirmed"] || "mdi-check-circle-outline"
    },
    bookingStateTitle(booking) {
      const labels = {
        approval_pending: "Termin bestätigt",
        meeting_pending: "Termin bestätigt",
        confirmed: "Termin bestätigt",
        meeting_failed: "Termin bestätigt",
        declined: "Termin nicht bestätigt",
      }
      return labels[booking.status || "confirmed"] || "Termin bestätigt"
    },
    bookingStateText(booking) {
      if (booking.status === "approval_pending") return "Der Teams-Link wird von der Academy-Leitung nachgereicht."
      if (booking.status === "meeting_pending") return "Der Teams-Link wird gerade für dich vorbereitet."
      if (booking.status === "meeting_failed") return "Der Teams-Link wird von der Academy-Leitung nachgereicht."
      if (booking.status === "declined") return booking.adminNote || "Wähle bei Bedarf einen anderen freien Termin."
      return booking.joinUrl || booking.teamsJoinUrl ? "Dein Teams-Link ist bereit." : "Alle Details sind in der App hinterlegt."
    },
    requestDate(request) {
      return request.startsAt || (request.booking && request.booking.startsAt) || ""
    },
    requestDuration(request) {
      if (request.durationMinutes) return request.durationMinutes
      const booking = request.booking || {}
      if (booking.durationMinutes) return booking.durationMinutes
      if (booking.startsAt && booking.endsAt) return Math.round((new Date(booking.endsAt) - new Date(booking.startsAt)) / 60000)
      return 60
    },
    requestJoinUrl(request) {
      const booking = request.booking || {}
      return booking.joinUrl || booking.teamsJoinUrl || (booking.teams && booking.teams.joinUrl) || ""
    },
    formatDayName(date) {
      return new Date(`${date}T12:00:00`).toLocaleDateString("de-CH", { weekday: "long" })
    },
    formatDate(date) {
      return new Date(`${date}T12:00:00`).toLocaleDateString("de-CH", { day: "2-digit", month: "long", year: "numeric" })
    },
    formatShortDate(date) {
      return new Date(`${date}T12:00:00`).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit" })
    },
    formatCompactDate(date) {
      return new Date(`${date}T12:00:00`).toLocaleDateString("de-CH", { day: "2-digit", month: "short" })
    },
    formatTime(date) {
      return new Date(date).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
    },
    formatTimeRange(start, end) {
      const fallbackEnd = new Date(new Date(start).getTime() + 30 * 60 * 1000)
      return `${this.formatTime(start)} - ${this.formatTime(end || fallbackEnd)}`
    },
    formatFullDate(date) {
      if (!date) return "-"
      return new Date(date).toLocaleString("de-CH", {
        weekday: "short",
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    },
    formatDayNumber(date) {
      return new Date(date).toLocaleDateString("de-CH", { day: "2-digit" })
    },
    formatMonth(date) {
      return new Date(date).toLocaleDateString("de-CH", { month: "short" })
    },
    formatWeekday(date) {
      return new Date(date).toLocaleDateString("de-CH", { weekday: "long", day: "2-digit", month: "long" })
    },
  },
}
</script>

<style scoped>
.coaching-page {
  --border: rgba(255, 255, 255, 0.1);
  --surface: rgba(255, 255, 255, 0.035);
  --muted: #a3a3a8;
  color: #f4f4f5;
  font-size: 1rem;
}

.hero-card,
.content-section {
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  background: var(--surface);
}

.hero-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.125rem;
  background: radial-gradient(circle at 88% 12%, rgba(139, 92, 246, 0.15), transparent 20rem), var(--surface);
}

.hero-heading {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 0.875rem;
}

.icon-action,
.hero-icon,
.small-icon-action {
  display: grid;
  place-items: center;
  border: 1px solid var(--border);
  color: #fff !important;
  background: rgba(255, 255, 255, 0.05) !important;
}

.icon-action,
.hero-icon {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border-radius: 0.875rem !important;
}

.hero-icon {
  color: #c4b5fd !important;
  background: rgba(139, 92, 246, 0.14) !important;
}

.hero-icon .v-icon {
  color: currentColor;
}

.eyebrow {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.hero-copy h2,
.section-header h3 {
  margin: 0.1875rem 0 0.375rem;
  color: #fff;
  font-size: 1.5rem;
  line-height: 1.3;
}

.hero-copy p,
.slots-header p {
  margin: 0;
  color: #c7c7cb;
  font-size: 1rem;
  line-height: 1.5;
}

.policy-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem 0.75rem;
  margin-top: 0.5rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.policy-row span {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.policy-row .v-icon {
  color: #7ee5a6;
  font-size: 1rem;
}

.extra-action,
.teams-action,
.primary-action {
  min-height: 2.75rem;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.extra-action {
  flex: 0 0 auto;
}

.content-section {
  margin-top: 1rem;
  padding: 1.125rem;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.section-header h3 {
  margin-bottom: 0;
  font-size: 1.25rem;
}

.count-badge,
.request-status {
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.06);
  font-size: 0.75rem;
  font-weight: 750;
}

.request-side {
  display: grid;
  justify-items: end;
  gap: 0.25rem;
}

.request-link {
  min-width: 0 !important;
  padding: 0 0.375rem !important;
  font-size: 0.75rem !important;
  text-transform: none;
}

.booking-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.booking-card {
  position: relative;
  display: flex;
  min-width: 0;
  overflow: hidden;
  flex-direction: column;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: radial-gradient(circle at 100% 0%, rgba(0, 150, 64, 0.08), transparent 17rem), rgba(255, 255, 255, 0.03);
}

.booking-card::before {
  position: absolute;
  inset: 0 auto 0 0;
  width: 0.25rem;
  background: #58d388;
  content: "";
}

.booking-card.is-approval_pending::before,
.booking-card.is-meeting_pending::before {
  background: #f5c451;
}

.booking-card.is-declined::before,
.booking-card.is-meeting_failed::before {
  background: #ff8d98;
}

.booking-card-header {
  display: grid;
  min-width: 0;
  grid-template-columns: 3.75rem minmax(0, 1fr);
  align-items: center;
  gap: 0.875rem;
}

.date-tile {
  display: grid;
  width: 3.75rem;
  height: 4rem;
  place-items: center;
  align-content: center;
  border-radius: 0.875rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.is-approval_pending .date-tile,
.is-meeting_pending .date-tile {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.is-declined .date-tile,
.is-meeting_failed .date-tile {
  color: #ff9aa5;
  background: rgba(244, 63, 94, 0.12);
}

.date-tile strong {
  font-size: 1.375rem;
  line-height: 1.1;
}

.date-tile small {
  font-size: 0.75rem;
  font-weight: 750;
  text-transform: uppercase;
}

.booking-copy {
  min-width: 0;
}

.booking-time-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.booking-time-row > span:first-child {
  color: #7ee5a6;
  font-size: 0.875rem;
  font-weight: 750;
}

.booking-status {
  display: inline-flex;
  min-height: 1.75rem;
  align-items: center;
  gap: 0.3125rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1.2;
}

.booking-status .v-icon {
  color: currentColor;
  font-size: 1rem;
}

.status-approval_pending,
.status-meeting_pending {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.status-meeting_failed,
.status-declined {
  color: #ff9aa5;
  background: rgba(244, 63, 94, 0.12);
}

.booking-copy h4 {
  margin: 0.1875rem 0;
  color: #fff;
  font-size: 1rem;
  line-height: 1.35;
}

.booking-copy p {
  display: flex;
  align-items: center;
  gap: 0.375rem 0.75rem;
  flex-wrap: wrap;
  margin: 0;
  color: var(--muted);
  font-size: 0.875rem;
}

.booking-copy p span {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.booking-copy p .v-icon {
  color: #7ee5a6;
  font-size: 1rem;
}

.booking-state {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  margin-top: 0.875rem;
  padding: 0.75rem;
  border: 1px solid rgba(88, 211, 136, 0.18);
  border-radius: 0.75rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.07);
}

.booking-state > .v-icon {
  color: currentColor;
  font-size: 1.25rem;
}

.booking-state strong,
.booking-state span {
  display: block;
}

.booking-state strong {
  color: #f4f4f5;
  font-size: 0.875rem;
}

.booking-state span {
  margin-top: 0.125rem;
  color: #c7c7cb;
  font-size: 0.875rem;
  line-height: 1.45;
}

.state-approval_pending,
.state-meeting_pending {
  border-color: rgba(245, 158, 11, 0.22);
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.07);
}

.state-meeting_failed,
.state-declined {
  border-color: rgba(244, 63, 94, 0.22);
  color: #ff9aa5;
  background: rgba(244, 63, 94, 0.07);
}

.booking-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.875rem;
  padding-top: 0.875rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  flex-wrap: wrap;
}

.cancel-action {
  margin-left: auto;
  min-width: 0 !important;
  padding: 0 0.5rem !important;
  font-size: 0.875rem !important;
  text-transform: none;
}

.empty-inline {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-height: 4rem;
  padding: 0.875rem;
  border: 1px dashed var(--border);
  border-radius: 0.875rem;
  color: var(--muted);
  font-size: 0.875rem;
}

.empty-inline .v-icon {
  color: #7ee5a6;
}

.slots-header {
  align-items: flex-end;
}

.slots-header p {
  color: var(--muted);
  font-size: 0.875rem;
}

.week-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #d4d4d8;
  font-size: 0.875rem;
  font-weight: 750;
  white-space: nowrap;
}

.small-icon-action {
  width: 2.5rem !important;
  min-width: 2.5rem !important;
  height: 2.5rem !important;
  border-radius: 0.75rem !important;
}

.slot-browser,
.slot-browser-skeleton {
  display: grid;
  gap: 0.75rem;
}

.slot-browser-skeleton {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.skeleton-card {
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.day-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.day-option {
  display: grid;
  min-width: 8rem;
  max-width: 12rem;
  min-height: 4.5rem;
  flex: 1 1 8rem;
  grid-template-areas:
    "weekday availability"
    "date availability";
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.125rem 0.5rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.875rem;
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.025);
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition: border-color 0.18s ease, background-color 0.18s ease;
}

.day-option > span {
  grid-area: weekday;
  overflow: hidden;
  color: var(--muted);
  font-size: 0.75rem;
  font-weight: 800;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.day-option strong {
  grid-area: date;
  color: #fff;
  font-size: 1rem;
  line-height: 1.25;
}

.day-option small {
  display: flex;
  grid-area: availability;
  align-items: center;
  gap: 0.3125rem;
  color: var(--muted);
  font-size: 0.75rem;
  font-weight: 750;
  white-space: nowrap;
}

.day-option small i {
  width: 0.4375rem;
  height: 0.4375rem;
  border-radius: 999px;
  background: #58d388;
  box-shadow: 0 0 0 0.1875rem rgba(88, 211, 136, 0.1);
}

.day-option.full small i {
  background: #8f9095;
  box-shadow: 0 0 0 0.1875rem rgba(143, 144, 149, 0.1);
}

.day-option.active {
  border-color: rgba(88, 211, 136, 0.45);
  color: #fff;
  background: rgba(0, 150, 64, 0.13);
  box-shadow: inset 0 0 0 1px rgba(88, 211, 136, 0.08);
}

.day-option.active > span,
.day-option.active small {
  color: #7ee5a6;
}

.day-option.active.full small {
  color: var(--muted);
}

.day-option:focus-visible {
  outline: 2px solid #7ee5a6;
  outline-offset: 2px;
}

@media (hover: hover) {
  .day-option:hover {
    border-color: rgba(88, 211, 136, 0.3);
    background: rgba(0, 150, 64, 0.08);
  }
}

.selected-day-card {
  min-width: 0;
  padding: 0.875rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: radial-gradient(circle at 92% 0%, rgba(0, 150, 64, 0.1), transparent 22rem), rgba(255, 255, 255, 0.025);
}

.selected-day-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.selected-day-heading > div > span {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.selected-day-heading h4 {
  margin: 0.125rem 0 0;
  color: #fff;
  font-size: 1.125rem;
  line-height: 1.3;
}

.selected-day-heading > small {
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.12);
  font-size: 0.75rem;
  font-weight: 750;
  white-space: nowrap;
}

.slot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(8.25rem, 1fr));
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.slot-button {
  display: grid;
  width: 100%;
  min-height: 3.25rem;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.8125rem;
  color: #fff;
  background: rgba(255, 255, 255, 0.035);
  cursor: pointer;
  text-align: left;
}

.slot-button.available {
  border-color: rgba(0, 150, 64, 0.3);
  background: rgba(0, 150, 64, 0.08);
}

.slot-button.occupied {
  color: #8f9095;
  cursor: not-allowed;
}

.slot-button > span:first-child {
  font-size: 1rem;
  font-weight: 800;
}

.slot-time small {
  display: block;
  margin-top: 0.125rem;
}

.slot-status {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.slot-button small {
  font-size: 0.75rem;
  font-weight: 750;
}

.slot-button.available small,
.slot-button.available .v-icon {
  color: #7ee5a6;
}

.slot-button .v-icon {
  color: currentColor;
  font-size: 1.125rem;
}

.empty-state {
  display: grid;
  min-height: 13rem;
  place-items: center;
  align-content: center;
  text-align: center;
}

.empty-state > span {
  display: grid;
  width: 3.5rem;
  height: 3.5rem;
  place-items: center;
  border-radius: 1rem;
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.14);
}

.empty-state > span .v-icon {
  color: currentColor;
}

.empty-state h4 {
  margin: 0.75rem 0 0.25rem;
  color: #fff;
  font-size: 1.125rem;
}

.empty-state p {
  margin: 0;
  color: var(--muted);
  font-size: 0.875rem;
}

.request-list {
  display: grid;
  gap: 0.625rem;
}

.request-card {
  display: grid;
  grid-template-columns: 2.75rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.875rem;
  background: rgba(255, 255, 255, 0.025);
}

.request-icon {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.14);
}

.request-icon .v-icon {
  color: currentColor;
}

.request-card strong {
  color: #fff;
  font-size: 0.9375rem;
}

.request-card p,
.request-card small {
  display: block;
  margin: 0.125rem 0 0;
  color: var(--muted);
  font-size: 0.75rem;
}

.request-card small {
  white-space: break-spaces;
  overflow-wrap: anywhere;
}

.status-confirmed {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.status-submitted {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.status-declined {
  color: #ff9aa5;
  background: rgba(244, 63, 94, 0.12);
}

.status-needs_info {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.dialog-card {
  color: #f4f4f5;
  background: #1a1a1a !important;
  font-size: 1rem;
}

.dialog-title {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.75rem;
  border-bottom: 1px solid var(--border);
}

.dialog-title > span,
.warning-icon {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.dialog-title > span .v-icon,
.warning-icon .v-icon {
  color: currentColor;
}

.dialog-title small {
  display: block;
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 750;
}

.dialog-title h3 {
  margin: 0;
  color: #fff;
  font-size: 1.125rem;
}

.dialog-body {
  padding: 1.25rem !important;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.dialog-actions {
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.25rem !important;
  border-top: 1px solid var(--border);
}

.confirm-card {
  padding: 1.5rem;
  text-align: center;
}

.warning-icon {
  margin: 0 auto;
  color: #ff8d98;
  background: rgba(244, 63, 94, 0.12);
}

.confirm-card h3 {
  margin: 0.875rem 0 0.25rem;
  color: #fff;
  font-size: 1.25rem;
}

.confirm-card p {
  margin: 0 0 1rem;
  color: var(--muted);
  font-size: 0.875rem;
}

.confirm-card > div {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

@media (max-width: 68rem) {
  .booking-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 47.99rem) {
  .hero-card,
  .slots-header {
    align-items: stretch;
    flex-direction: column;
  }

  .extra-action {
    width: 100%;
  }

  .slot-browser-skeleton {
    grid-template-columns: 1fr;
  }

  .week-controls {
    justify-content: space-between;
  }

  .selected-day-heading {
    align-items: flex-start;
    flex-wrap: wrap;
  }
}

@media (max-width: 37.49rem) {
  .hero-heading {
    display: grid;
    grid-template-columns: 2.75rem minmax(0, 1fr);
  }

  .hero-icon {
    display: none;
  }

  .booking-grid {
    grid-template-columns: 1fr;
  }

  .booking-card-header {
    grid-template-columns: 3.75rem minmax(0, 1fr);
  }

  .teams-action {
    flex: 1 1 100%;
  }

  .request-card {
    grid-template-columns: 2.75rem minmax(0, 1fr);
  }

  .request-side {
    grid-column: 2;
    justify-self: start;
    justify-items: start;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .dialog-actions {
    flex-wrap: wrap;
  }

  .dialog-actions .v-btn {
    flex: 1 1 auto;
  }
}

@media (max-width: 22.4375rem) {
  .day-option {
    max-width: none;
    flex-basis: 100%;
  }

  .slot-grid {
    grid-template-columns: 1fr;
  }
}
</style>
